(() => {
  const root = document.querySelector('[data-mori-popup-offer]');

  if (!root || window.__moriPopupOfferInitialized) return;
  window.__moriPopupOfferInitialized = true;

  const STORAGE_KEY = 'mori_popup_offer_state';
  const SESSION_KEY = 'mori_popup_offer_shown';

  const delaySeconds = Number(root.dataset.delaySeconds || 20);
  const suppressDays = Number(root.dataset.suppressDays || 7);
  const productUrl = root.dataset.productUrl;
  const excludedTemplates = (root.dataset.excludedTemplates || '')
    .split(',')
    .map((template) => template.trim())
    .filter(Boolean);

  const currentTemplate = () => document.querySelector('main[data-template]')?.dataset.template || '';

  const gameStage = root.querySelector('[data-mori-popup-stage="game"]');
  const winStage = root.querySelector('[data-mori-popup-stage="win"]');
  const cards = Array.from(root.querySelectorAll('[data-mori-popup-card]'));
  const form = root.querySelector('#MoriPopupOfferForm');
  const formError = root.querySelector('[data-mori-popup-form-error]');

  const readState = () => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    } catch (error) {
      return {};
    }
  };

  const writeState = (patch) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readState(), ...patch }));
    } catch (error) {
      /* storage unavailable (private mode, quota): suppression falls back to this session only */
    }
  };

  const alreadyShownThisSession = () => {
    try {
      return sessionStorage.getItem(SESSION_KEY) === '1';
    } catch (error) {
      return false;
    }
  };

  const markShownThisSession = () => {
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch (error) {
      /* ignore */
    }
  };

  const isSuppressed = () => {
    const state = readState();
    if (state.claimed) return true;
    if (state.suppressedUntil && Date.now() < state.suppressedUntil) return true;
    return false;
  };

  const isExcludedPage = () => {
    const template = currentTemplate();
    return excludedTemplates.some((excluded) => template === excluded || template.startsWith(`${excluded}.`));
  };

  // First dismiss earns one more show in a later session; the second dismiss starts the suppression window.
  const showNumber = () => Math.min((readState().dismissCount || 0) + 1, 2);

  const track = (eventName, properties = {}) => {
    if (!window.posthog) return;
    window.posthog.capture(eventName, { popup: 'match_cards', ...properties });
  };

  const showPopup = () => {
    if (root.classList.contains('is-visible')) return;

    root.hidden = false;
    // Force layout before adding the transition class so the entrance animation runs.
    void root.offsetHeight;
    root.classList.add('is-visible');
    markShownThisSession();
    track('popup_match_cards_viewed', { show_number: showNumber() });

    document.addEventListener('keydown', handleKeydown);
  };

  const hidePopup = () => {
    root.classList.remove('is-visible');
    document.removeEventListener('keydown', handleKeydown);

    window.setTimeout(() => {
      root.hidden = true;
    }, 300);

    if (showNumber() === 1) {
      writeState({ dismissCount: 1, lastDismissedAt: Date.now() });
    } else {
      writeState({
        dismissCount: 2,
        lastDismissedAt: Date.now(),
        suppressedUntil: Date.now() + suppressDays * 24 * 60 * 60 * 1000,
      });
    }
  };

  // Closes after a sign-up. Unlike hidePopup it records no dismiss: the claim already suppresses it.
  const closePopup = () => {
    root.classList.remove('is-visible');
    document.removeEventListener('keydown', handleKeydown);
    window.setTimeout(() => {
      root.hidden = true;
    }, 300);
  };

  const handleKeydown = (event) => {
    if (event.key === 'Escape') {
      track('popup_match_cards_dismissed', { stage: winStage.hidden ? 'game' : 'win', method: 'escape_key', show_number: showNumber() });
      hidePopup();
    }
  };

  root.querySelectorAll('[data-mori-popup-dismiss]').forEach((dismissEl) => {
    dismissEl.addEventListener('click', () => {
      track('popup_match_cards_dismissed', { stage: winStage.hidden ? 'game' : 'win', method: 'close_button', show_number: showNumber() });
      hidePopup();
    });
  });

  const flippedCards = [];
  let gameLocked = false;

  cards.forEach((card) => {
    card.addEventListener('click', () => {
      if (gameLocked || card.classList.contains('is-flipped')) return;

      card.classList.add('is-flipped');
      flippedCards.push(card);
      track('popup_match_cards_card_flipped', { card_index: card.dataset.index, flip_number: flippedCards.length });

      if (flippedCards.length === 2) {
        gameLocked = true;
        cards.forEach((otherCard) => {
          if (!otherCard.classList.contains('is-flipped')) otherCard.disabled = true;
        });
        track('popup_match_cards_win_revealed');

        window.setTimeout(() => {
          gameStage.hidden = true;
          winStage.hidden = false;
          root.querySelector('#MoriPopupOfferEmail')?.focus();
        }, 900);
      }
    });
  });

  if (form) {
    /*
      Shopify's hCaptcha (content_for_header) binds to this form the first time it's touched: its
      submit listener stops the event, puts a token in h-captcha-response, then calls the
      form.submit it captured when binding. Our fetch used to go straight from the submit event,
      before the token existed, and Shopify silently dropped about one sign-up in five. So
      form.submit is replaced here, before the captcha binds, with the background send, and the
      captcha calls it once the token is in. If the captcha never binds (script blocked, or turned
      off in admin) the send runs straight from the submit event as before.
    */
    let flagCart;
    let sent = false;
    let resultTracked = false;

    const finish = () => {
      track('popup_match_cards_continue_clicked');
      // On a product page they stay put: a reload would drop their tin count, plan, scroll
      // position and the landing query string. The cart is already flagged, so the gift sync
      // adds the whisk to whatever they add next.
      if (currentTemplate().startsWith('product')) {
        closePopup();
        return;
      }
      window.location.href = productUrl ? `${productUrl}?offer=whisk` : '/collections/all';
    };

    const trackResult = (properties) => {
      if (resultTracked) return;
      resultTracked = true;
      track('popup_signup_result', properties);
    };

    /*
      About one sign-up in five, the captcha posts the form natively instead of calling the send
      below, and the whole page reloaded on ?customer_posted=true. The form now targets a hidden
      iframe (sandboxed, so the page that comes back runs no scripts or analytics), which keeps
      that post off the visitor's page. Its load counts as the send so the 5s fallback doesn't
      post a second time.
    */
    const postFrame = document.createElement('iframe');
    postFrame.name = 'MoriPopupOfferPost';
    postFrame.title = 'Sign-up';
    postFrame.hidden = true;
    postFrame.tabIndex = -1;
    postFrame.setAttribute('aria-hidden', 'true');
    postFrame.setAttribute('sandbox', 'allow-same-origin');
    postFrame.addEventListener('load', () => {
      if (!flagCart) return;
      let frameUrl = '';
      try {
        frameUrl = postFrame.contentWindow.location.href;
      } catch (error) {
        /* unreadable: leave challenged false */
      }
      if (!frameUrl || frameUrl === 'about:blank') return;
      const alreadySent = sent;
      sent = true;
      trackResult({
        has_captcha_token: Boolean(form.elements['h-captcha-response']?.value),
        challenged: frameUrl.includes('/challenge'),
        native_post: true,
      });
      if (!alreadySent) Promise.allSettled([flagCart]).finally(finish);
    });
    root.appendChild(postFrame);
    form.target = postFrame.name;

    const sendSignUp = () => {
      if (sent) return;
      sent = true;
      const hasToken = Boolean(form.elements['h-captcha-response']?.value);
      const signUp = fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        credentials: 'same-origin',
      }).then((response) => {
        // A dropped sign-up ends on /challenge instead of creating the customer.
        trackResult({
          has_captcha_token: hasToken,
          challenged: response.url.includes('/challenge'),
          status: response.status,
        });
      });

      Promise.allSettled([flagCart, signUp]).finally(finish);
    };

    try {
      form.submit = sendSignUp;
    } catch (error) {
      /* not assignable: the captcha falls back to a native submit, which still signs them up */
    }

    form.addEventListener('submit', (event) => {
      event.preventDefault();

      const emailInput = form.querySelector('input[name="contact[email]"]');
      const email = emailInput?.value.trim() || '';
      const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

      if (formError) formError.hidden = true;

      // Double taps and invalid addresses stop here, before the captcha's listener runs.
      if (sent || flagCart) {
        event.stopImmediatePropagation();
        return;
      }

      if (!isValidEmail) {
        event.stopImmediatePropagation();
        if (formError) {
          formError.textContent = 'Enter a valid email address';
          formError.hidden = false;
        }
        emailInput?.focus();
        return;
      }

      const submitButton = form.querySelector('button[type="submit"]');
      if (submitButton) submitButton.disabled = true;

      track('popup_match_cards_email_submitted');
      track('popup_email_submitted', { source: 'match_cards' });
      // Same identity the order webhook uses, so popup sign-ups join up with their purchases.
      window.posthog?.identify?.(email.toLowerCase(), { email: email.toLowerCase() });
      writeState({ claimed: true });

      // Flags the cart so the gift sync (snippets/mori-cart-gifts.liquid) adds
      // the free whisk to any Mori Matcha order, one-off included.
      flagCart = fetch(`${window.routes?.cart_update_url || '/cart/update'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { _mori_whisk_claim: '1' } }),
      }).then((response) => {
        // No reload any more to pick the flag up, so the gift sync re-reads the cart now: a tin
        // already in it gets the whisk, and the next add plans its gifts against a fresh cart.
        window.__moriSyncGifts?.();
        return response;
      });

      // Source and channel onto their Klaviyo profile, sent in the background (mori-analytics.js).
      window.__moriSendSourceToKlaviyo?.(email.toLowerCase());

      // No captcha on the form: send now. Otherwise the captcha's listener, which runs after this
      // one, gets the token and calls sendSignUp. If that stalls, send after 5s rather than strand
      // the visitor, unless an hCaptcha challenge is open for them to solve.
      if (!form.hasAttribute('data-hcaptcha-bound')) {
        sendSignUp();
        return;
      }
      const fallback = () => {
        if (sent) return;
        // hCaptcha keeps its challenge frame in the page while closed, hidden and off screen.
        const challengeOpen = [...document.querySelectorAll('iframe[src*="hcaptcha"]')].some((frame) => {
          const rect = frame.getBoundingClientRect();
          return (
            rect.height > 100 &&
            rect.bottom > 0 &&
            rect.top < window.innerHeight &&
            getComputedStyle(frame).visibility === 'visible'
          );
        });
        if (challengeOpen) window.setTimeout(fallback, 2000);
        else sendSignUp();
      };
      window.setTimeout(fallback, 5000);
    });
  }

  let delayTimer;
  let priceBox;

  const cartDrawer = () => document.querySelector('cart-drawer');
  const isCartDrawerOpen = () => Boolean(cartDrawer()?.classList.contains('active'));

  const clearTriggers = () => {
    window.removeEventListener('scroll', onScroll);
    window.clearTimeout(delayTimer);
  };

  // Never open over the cart drawer: wait for it to close, then show.
  const showWhenCartClosed = () => {
    if (!isCartDrawerOpen()) {
      showPopup();
      return;
    }

    const observer = new MutationObserver(() => {
      if (isCartDrawerOpen()) return;
      observer.disconnect();
      showPopup();
    });
    observer.observe(cartDrawer(), { attributes: true, attributeFilter: ['class'] });
  };

  const trigger = () => {
    clearTriggers();
    showWhenCartClosed();
  };

  // On product pages, fire once the price box has scrolled out of view above the viewport.
  // Pages without one fall back to scrolling past half the page.
  const onScroll = () => {
    if (priceBox) {
      if (priceBox.getBoundingClientRect().bottom < 0) trigger();
      return;
    }

    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollable > 0 && window.scrollY / scrollable >= 0.5) trigger();
  };

  const findPriceBox = () => {
    if (!currentTemplate().startsWith('product')) return null;
    return (
      document.querySelector('#MainContent .mori-purchase-builder') ||
      document.querySelector('#MainContent [id^="price-template"]')
    );
  };

  const armTriggers = () => {
    root.querySelectorAll('img[data-src]').forEach((img) => {
      img.src = img.dataset.src;
    });

    priceBox = findPriceBox();
    window.addEventListener('scroll', onScroll, { passive: true });
    delayTimer = window.setTimeout(trigger, delaySeconds * 1000);
  };

  const init = () => {
    // ?popup_preview=1 ignores dismiss/claim suppression for reviewing; ?popup_preview=0 hides it.
    const previewParam = new URLSearchParams(window.location.search).get('popup_preview');
    if (previewParam === '0' || isExcludedPage()) return;
    if (previewParam !== '1' && (isSuppressed() || alreadyShownThisSession())) return;

    armTriggers();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
